import { useState, useCallback, useMemo } from 'react';

type Validator = (value: string) => string | null;
type ValidationSchema<T> = Partial<Record<keyof T, Validator>>;

export function useFormValidation<T extends Record<string, any>>(
  initialValues: T,
  schema: ValidationSchema<T>
) {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof T, boolean>>>({});

  const validateField = useCallback((name: keyof T, value: any): string | null => {
    const validator = schema[name];
    if (!validator) return null;
    return validator(value?.toString() || '');
  }, [schema]);

  const setValue = useCallback((name: keyof T, value: any) => {
    setValues(prev => ({ ...prev, [name]: value }));
    // Live validation
    if (touched[name]) {
      const err = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: err || undefined }));
    }
  }, [touched, validateField]);

  const setFieldTouched = useCallback((name: keyof T) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const err = validateField(name, values[name]);
    setErrors(prev => ({ ...prev, [name]: err || undefined }));
  }, [values, validateField]);

  const validateAll = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof T, string>> = {};
    const newTouched: Partial<Record<keyof T, boolean>> = {};
    let valid = true;

    for (const key in schema) {
      newTouched[key] = true;
      const err = validateField(key, values[key]);
      if (err) {
        newErrors[key] = err;
        valid = false;
      }
    }

    setTouched(newTouched);
    setErrors(newErrors);
    return valid;
  }, [schema, values, validateField]);

  const reset = useCallback((newValues?: T) => {
    setValues(newValues || initialValues);
    setErrors({});
    setTouched({});
  }, [initialValues]);

  const isValid = useMemo(() => {
    for (const key in schema) {
      if (validateField(key, values[key])) return false;
    }
    return true;
  }, [values, schema, validateField]);

  return {
    values,
    errors,
    touched,
    isValid,
    setValue,
    setFieldTouched,
    validateAll,
    reset,
    setValues,
  };
}
